import "./Products.scss";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect } from "react";
import GreenButton from "../../components/GreenButton/GreenButton";
import BlueButton from "../../components/BlueButton/BlueButton";
import AddGif from "./../../assets/add-gif.gif";
import Upload from "./../../assets/upload-excel.svg";
import Download from "./../../assets/download-excel.svg";
import SearchIcon from "@mui/icons-material/Search";
import ShowUser from "./../../assets/show-user.svg";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import { DatePicker, message } from "antd";
import { DataGrid } from "@mui/x-data-grid";
import { ProductsColumns } from "../../SourceData";
import { Link } from "react-router-dom";
import api from "../../utils/api";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import MessageModals from "../../components/MessageModals/MessageModals";
import usePermissions from "../../hooks/usePermissions";
const { RangePicker } = DatePicker;

const Products = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  const [cities, setCities] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  // Filter states
  const [query, setQuery] = useState("");
  const [view, setView] = useState("sahm");
  const [dateRange, setDateRange] = useState(null);
  const [category, setCategory] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [hasDiscount, setHasDiscount] = useState(false);
  const [isOutOfStock, setIsOutOfStock] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");

  // Fetch countries data
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get("/countries");
        setCountries(res.data.data);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    getCountries();
    localStorage.setItem("type", "sahm");
  }, []);

  // Fetch main categories with type filtering
  useEffect(() => {
    const getCategories = async () => {
      try {
        // Create params to filter by type
        const params = new URLSearchParams({
          // type: view,
          flag: 1, // Get only main categories (flag 1)
          active: 1, // Get only active categories
        });

        const res = await api.get(`/categories?${params.toString()}`);
        setCategories(res.data.data);
      } catch (error) {
        console.error("Error fetching main categories:", error);
      }
    };
    getCategories();
  }, [view]); // Re-fetch whenever view changes

  // Fetch subcategories when main category changes
  useEffect(() => {
    const getSubCategories = async () => {
      if (!category) {
        setGovernorates([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          // type: view,
          flag: 2, // Get subcategories (flag 2)
          active: 1, // Get only active categories
          parent_id: category, // Filter by the selected main category
        });

        const res = await api.get(`/categories?${params.toString()}`);
        setGovernorates(res.data.data);

        // Reset selections for subcategory and inner category
        setProvince("");
        setCity("");
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    };

    getSubCategories();
  }, [category, view]);

  // Fetch inner categories when subcategory changes
  useEffect(() => {
    const getInnerCategories = async () => {
      if (!province) {
        setCities([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          // type: view,
          flag: 3, // Get inner categories (flag 3)
          active: 1, // Get only active categories
          parent_id: province, // Filter by the selected subcategory
        });

        const res = await api.get(`/categories?${params.toString()}`);
        setCities(res.data.data);

        // Reset inner category selection
        setCity("");
      } catch (error) {
        console.error("Error fetching inner categories:", error);
      }
    };

    getInnerCategories();
  }, [province, view]);

  // Fetch products with filters
  const getProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1, // API expects 1-based indexing
        perPage: pageSize, // Use "limit" parameter instead of "per_page"
        type: view,
      });

      if (query.length >= 2) {
        params.append("search", query);
      }

      if (country) {
        params.append("country_id", country);
      }

      if (category) {
        params.append("category_id", category);
      }
      if (province) {
        params.append("mid_category_id", province);
      }
      if (city) {
        params.append("sub_category_id", city);
      }

      if (hasDiscount) {
        params.append("has_discount", true);
      }
      if (isOutOfStock) {
        params.append("out_of_stock", true);
      }
      if (dateRange) {
        if (dateRange[0]) {
          params.append("date_from", dateRange[0].format("YYYY-MM-DD"));
        }
        if (dateRange[1]) {
          params.append("date_to", dateRange[1].format("YYYY-MM-DD"));
        }
      }

      const res = await api.get(`/products_admin?${params.toString()}`);

      // Update this section to match the new API response format
      if (res.data && res.data.status === "success") {
        setProducts(res.data.data || []);
        setTotalRows(res.data.total || 0);
      } else {
        console.error("Unexpected API response format:", res.data);
        setProducts([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch products when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.length >= 2 || query.length === 0) {
        getProducts();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    query,
    view,
    category,
    province,
    city,
    dateRange,
    hasDiscount,
    isOutOfStock,
    page,
    pageSize,
    country,
  ]);

  const handleActive = async (id) => {
    setLoading(true);
    try {
      await api.put(`/products/${id}/activateToggle`);
      getProducts();
      message.success("تم تعديل حالة المنتج بنجاح");
    } catch (error) {
      console.error("Error toggling product status:", error);
      message.error("حدث خطأ أثناء تعديل حالة المنتج");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatured = async (id) => {
    setLoading(true);
    try {
      await api.put(`/products/${id}/activateToggle?flag=featured`);
      getProducts();
      message.success("تم تعديل حالة المنتج بنجاح");
    } catch (error) {
      console.error("Error toggling product status:", error);
      message.error("حدث خطأ أثناء تعديل حالة المنتج");
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleOpenDelete = (product) => {
    setSelectedProduct(product);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedProduct(null);
  };

  const actionColumn = [
    hasPermission(79) && {
      field: "featured",
      headerName: "المنتجات المقترحة",
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive =
          params?.row?.featured === 1 || params?.row?.featured === "1";
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleFeatured(params.row.id)}
          />
        );
      },
    },
    hasPermission(79) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive =
          params?.row?.active === 1 || params?.row?.active === "1";
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row.id)}
          />
        );
      },
    },

    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(83) && (
              <Link className="cell-btn" to={`/products/${params.row.id}`}>
                <img src={ShowUser} alt="" />
              </Link>
            )}
            {hasPermission(81) && (
              <Link className="cell-btn" to={`/products/edit/${params.row.id}`}>
                <img src={EditUser} alt="" />
              </Link>
            )}
            {hasPermission(82) && (
              <div
                className="cell-btn"
                onClick={() => handleOpenDelete(params.row)}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean); // Filter out any false values from conditional rendering

  return (
    <div className="products">
      <div className="special-header">
        <p className="title">المنتجات</p>

        <div className="btns-container">
          <GreenButton
            text="منتجات السهم العربي"
            size={isMobile ? "full" : "large"}
            variant={view === "sahm" ? "fill" : "outlined"}
            onClick={() => setView("sahm")}
          />

          <GreenButton
            text="منتجات المتاجر"
            size={isMobile ? "full" : "medium"}
            variant={view === "shop" ? "fill" : "outlined"}
            onClick={() => {
              setView("shop");
              localStorage.setItem("type", "shop");
            }}
          />

          <GreenButton
            text="منتجات المصانع"
            size={isMobile ? "full" : "medium"}
            variant={view === "factory" ? "fill" : "outlined"}
            onClick={() => {
              setView("factory");
              localStorage.setItem("type", "factory");
            }}
          />
        </div>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="بحث..."
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <RangePicker
              format={"MMM D"}
              style={{
                width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                padding: "0px 10px",
                direction: "ltr",
              }}
              placeholder={["من", "الي"]}
              className="range-picker"
              onChange={setDateRange}
              value={dateRange}
            />
          </div>
          {hasPermission(80) && (
            <BlueButton
              text="اضافة منتج جديد"
              size={isMobile ? "full" : isTablet ? "full" : "large"}
              variant="fill"
              path="/products/create"
              icon={<img src={AddGif} alt="" />}
            />
          )}
        </div>

        {/* Filters Container */}
        <div className="filters-container">
          <div className="filters">
            {/* Main Categories Filter */}
            <FormControl
              sx={{
                width: isMobile ? "100%" : "140px",
              }}
            >
              <InputLabel
                htmlFor="category"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                القسم الرئيسي
              </InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                displayEmpty={true}
                sx={{ borderRadius: "8px" }}
              >
                <MenuItem value="">الجميع</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.title_ar || cat.title?.ar || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sub Categories Filter */}
            <FormControl
              sx={{
                width: isMobile ? "100%" : "140px",
              }}
            >
              <InputLabel
                htmlFor="subcategory"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                القسم الفرعي
              </InputLabel>
              <Select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                displayEmpty={true}
                sx={{ borderRadius: "8px" }}
                disabled={!category}
              >
                <MenuItem value="">الجميع</MenuItem>
                {governorates.map((subcat) => (
                  <MenuItem key={subcat.id} value={subcat.id}>
                    {subcat.title_ar || subcat.title?.ar || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Inner Categories Filter */}
            <FormControl
              sx={{
                width: isMobile ? "100%" : "140px",
              }}
            >
              <InputLabel
                htmlFor="innercategory"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                القسم الداخلي
              </InputLabel>
              <Select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                displayEmpty={true}
                sx={{ borderRadius: "8px" }}
                disabled={!province}
              >
                <MenuItem value="">الجميع</MenuItem>
                {cities.map((innercat) => (
                  <MenuItem key={innercat.id} value={innercat.id}>
                    {innercat.title_ar || innercat.title?.ar || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Country Filter */}
            {view !== "sahm" && (
              <FormControl
                sx={{
                  width: isMobile ? "100%" : "140px",
                }}
              >
                <InputLabel
                  htmlFor="innercategory"
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  نطاقات العمل
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  displayEmpty={true}
                  sx={{ borderRadius: "8px" }}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <MenuItem value="">الجميع</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name_ar}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>

          <div className="excels-actions">
            {hasPermission(87) && <img src={Upload} alt="" />}
            {hasPermission(86) && <img src={Download} alt="" />}
          </div>
        </div>

        <div className="checked-container">
          <div className="item">
            <input
              type="checkbox"
              checked={hasDiscount}
              onChange={(e) => setHasDiscount(e.target.checked)}
            />
            <label>المنتجات التي تحتوي على خصم</label>
          </div>
          <div className="item">
            <input
              type="checkbox"
              checked={isOutOfStock}
              onChange={(e) => setIsOutOfStock(e.target.checked)}
            />
            <label>المنتجات المنتهية</label>
          </div>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={ProductsColumns.concat(actionColumn)}
          rows={products.map((row, index) => ({
            ...row,
            index: page * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </div>
      <MessageModals
        open={openDelete}
        currentView="delete"
        route={`/products/${selectedProduct?.id}`}
        onClose={handleCloseDelete}
        onDataChange={getProducts}
        text={"تم حذف المنتج بنجاح"}
      />
    </div>
  );
};

export default Products;
